import React from 'react';
import { Rocket, FlaskConical, ChevronLeft, AlertTriangle } from 'lucide-react';
import { Link, useLocation, Outlet } from 'react-router-dom';

interface LaunchpadLayoutProps {
  onBackToApp?: () => void;
}

export default function LaunchpadLayout({ onBackToApp }: LaunchpadLayoutProps) {
  const location = useLocation();

  const handleBack = () => {
    if (onBackToApp) {
      onBackToApp();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-emerald-50 selection:bg-emerald-500/30">
      {/* Sticky Disclaimer Banner */}
      <div className="sticky top-0 z-[110] w-full bg-emerald-600 text-black px-4 py-2 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 text-[10px] md:text-xs font-black uppercase tracking-widest text-center">
          <div className="flex items-center gap-2">
            <FlaskConical size={14} />
            <span>BrainRotBlast Labs — Launchpad (Beta)</span>
          </div>
          <div className="hidden md:block w-px h-4 bg-black/20" />
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>Not financial advice. Experimental. Transparent token launches.</span>
          </div>
        </div>
      </div>

      {/* Launchpad Navigation */}
      <nav className="bg-zinc-900/50 backdrop-blur-xl border-b border-emerald-500/20 px-4 py-4 sticky top-[40px] md:top-[32px] z-[105]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bungee text-xs text-zinc-400 transition-all group"
            >
              <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Back to Image Generator
            </button>
            <div className="hidden md:flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <Rocket size={20} className="text-emerald-400" />
              </div>
              <div>
                <h1 className="font-bungee text-xl text-emerald-400 leading-none">LAUNCHPAD</h1>
                <span className="text-[10px] font-black text-emerald-500/50 uppercase tracking-[0.2em]">Labs Program</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <Link 
              to="/launchpad" 
              className={`px-4 py-2 rounded-xl font-bungee text-xs transition-all ${location.pathname === '/launchpad' ? 'bg-emerald-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 'text-zinc-500 hover:text-emerald-400'}`}
            >
              Launches
            </Link>
            <Link 
              to="/launchpad/about" 
              className={`px-4 py-2 rounded-xl font-bungee text-xs transition-all ${location.pathname === '/launchpad/about' ? 'bg-emerald-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 'text-zinc-500 hover:text-emerald-400'}`}
            >
              About
            </Link>
            <Link 
              to="/launchpad/policy" 
              className={`px-4 py-2 rounded-xl font-bungee text-xs transition-all ${location.pathname === '/launchpad/policy' ? 'bg-emerald-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 'text-zinc-500 hover:text-emerald-400'}`}
            >
              Policy
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 md:p-8 animate-in fade-in duration-500">
        <Outlet />
      </main>

      <footer className="max-w-7xl mx-auto p-8 border-t border-emerald-500/10 text-center space-y-4">
        <div className="flex items-center justify-center gap-2 text-emerald-500/30 font-bungee text-[10px] uppercase tracking-widest">
          <FlaskConical size={12} />
          <span>Experimental Labs Module</span>
        </div>
        <p className="text-zinc-600 text-[10px] max-w-2xl mx-auto leading-relaxed">
          Launchpad is a research project. Participation involves high risk. No guarantees of value or liquidity are provided. 
          All tokenomics and founder wallets are public for maximum transparency.
        </p>
      </footer>
    </div>
  );
}
